import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import toast from "react-hot-toast";

const emptyCase = {
  customerId: "",
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  trailerId: "",
  description: "",
  status: "AANWEZIG",
  photos: "[]",
  damageCost: ""
};

const parseList = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function DamageUpload() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(true);
  const [form, setForm] = useState(emptyCase);

  const { data: damageCases = [], isLoading } = useQuery({
    queryKey: ["damage-cases"],
    queryFn: async () => (await API.get("/damage-cases")).data
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await API.get("/customers")).data
  });

  const { data: trailers = [] } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => (await API.get("/trailers")).data
  });

  const photos = parseList(form.photos);
  const selectedCase = useMemo(
    () => damageCases.find((damageCase) => damageCase.id === selectedId),
    [damageCases, selectedId]
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return damageCases.filter((damageCase) => (
      damageCase.customerName.toLowerCase().includes(term) ||
      (damageCase.customerPhone || "").toLowerCase().includes(term) ||
      (damageCase.trailer?.name || "").toLowerCase().includes(term)
    ));
  }, [damageCases, search]);

  const createMutation = useMutation({
    mutationFn: (payload) => API.post("/damage-cases", payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      setSelectedId(res.data.id);
      setIsCreating(false);
      toast.success("Schadegeval aangemaakt");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Schadegeval aanmaken mislukt")
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => API.patch(`/damage-cases/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      toast.success("Schadegeval opgeslagen");
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      if (error.response?.status === 404) {
        setSelectedId(null);
        setIsCreating(true);
        setForm(emptyCase);
      }
      toast.error(error.response?.data?.error || "Schadegeval opslaan mislukt");
    }
  });

  const archiveMutation = useMutation({
    mutationFn: (id) => API.post(`/damage-cases/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      setSelectedId(null);
      setIsCreating(true);
      setForm(emptyCase);
      toast.success("Schadegeval gearchiveerd");
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      toast.error(error.response?.data?.error || "Schadegeval archiveren mislukt");
    }
  });

  const deletePhotoMutation = useMutation({
    mutationFn: ({ id, path }) => API.delete(`/damage-cases/${id}/photos`, { data: { path } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["damage-cases"] });
      setForm((current) => ({
        ...current,
        photos: res.data.photos || current.photos
      }));
      toast.success("Foto verwijderd");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Foto verwijderen mislukt");
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("image", file);
        uploaded.push((await API.post("/upload", formData)).data.filePath);
      }
      return uploaded;
    },
    onSuccess: (filePaths) => {
      setForm({ ...form, photos: JSON.stringify([...photos, ...filePaths]) });
      toast.success(filePaths.length === 1 ? "Foto toegevoegd" : `${filePaths.length} foto's toegevoegd`);
    },
    onError: () => toast.error("Een of meerdere foto's uploaden is mislukt")
  });

  const selectCustomer = (customerId) => {
    const customer = customers.find((item) => item.id === Number(customerId));
    setForm({
      ...form,
      customerId,
      customerName: customer ? `${customer.firstName || ""} ${customer.lastName}`.trim() : "",
      customerPhone: customer?.phone || "",
      customerEmail: customer?.email || ""
    });
  };

  const selectDamageCase = (damageCase) => {
    setSelectedId(damageCase.id);
    setIsCreating(false);
    setForm({
      customerId: damageCase.customerId ? String(damageCase.customerId) : "",
      customerName: damageCase.customerName || "",
      customerPhone: damageCase.customerPhone || "",
      customerEmail: damageCase.customerEmail || "",
      trailerId: damageCase.trailerId ? String(damageCase.trailerId) : "",
      description: damageCase.description || "",
      status: damageCase.status || "AANWEZIG",
      photos: damageCase.photos || "[]",
      damageCost: damageCase.damageCost ?? ""
    });
  };

  const newDamageCase = () => {
    setSelectedId(null);
    setIsCreating(true);
    setForm(emptyCase);
  };

  const saveDamageCase = () => {
    if (isCreating || !selectedCase) {
      createMutation.mutate(form);
      return;
    }

    updateMutation.mutate({ id: selectedCase.id, payload: form });
  };

  const removePhoto = (photo) => {
    if (selectedCase?.id && window.confirm("Deze foto verwijderen?")) {
      deletePhotoMutation.mutate({ id: selectedCase.id, path: photo });
      return;
    }

    setForm({
      ...form,
      photos: JSON.stringify(photos.filter((item) => item !== photo))
    });
  };

  useEffect(() => {
    if (isCreating || !selectedId) return;
    if (!selectedCase && damageCases.length) {
      selectDamageCase(damageCases[0]);
    }
  }, [damageCases, selectedCase, selectedId, isCreating]);

  if (isLoading) return <div className="loading-state">Schadegevallen laden...</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Schadegevallen</h1>
          <p className="page-subtitle">Maak schadegevallen aan, pas ze aan en zoek op klant of trailer.</p>
        </div>
        <button className="button secondary" onClick={newDamageCase}>Nieuw schadegeval</button>
      </header>

      <div className="reservation-workbench">
        <div>
          <div className="panel panel-pad">
            <input className="input" placeholder="Zoek op klant, telefoonnummer of trailer" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          <div className="list">
            {filtered.length === 0 && <div className="panel empty-state">Geen schadegevallen gevonden.</div>}
            {filtered.map((damageCase) => (
              <button
                key={damageCase.id}
                className={`list-row reservation-row${selectedId === damageCase.id ? " selected" : ""}`}
                onClick={() => selectDamageCase(damageCase)}
              >
                <div>
                  <div className="row-title">{damageCase.customerName}</div>
                  <div className="row-meta">{damageCase.customerPhone || "-"} - {damageCase.trailer?.name || "Geen trailer"}</div>
                  <div className="status-line">
                    <span className="badge danger">EUR {damageCase.damageCost || 0}</span>
                    <span className="badge info">{damageCase.status}</span>
                  </div>
                  {damageCase.description && <p className="page-subtitle">{damageCase.description}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>

      <aside className="panel panel-pad detail-panel">
        <h3 className="detail-title">{isCreating ? "Nieuw schadegeval" : "Schadegeval bewerken"}</h3>
        <div className="form-grid">
          <label className="field">
            <span className="field-label">Klant zoeken/selecteren</span>
            <select className="select" value={form.customerId} onChange={(e) => selectCustomer(e.target.value)}>
              <option value="">Selecteer klant</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.firstName} {customer.lastName} - {customer.phone}</option>
              ))}
            </select>
          </label>
          <label className="field"><span className="field-label">Klantnaam</span><input className="input" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
          <label className="field"><span className="field-label">Telefoonnummer</span><input className="input" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} /></label>
          <label className="field">
            <span className="field-label">Trailer</span>
            <select className="select" value={form.trailerId} onChange={(e) => setForm({ ...form, trailerId: e.target.value })}>
              <option value="">Selecteer trailer</option>
              {trailers.map((trailer) => <option key={trailer.id} value={trailer.id}>{trailer.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Status</span>
            <select className="select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="AANWEZIG">Schade nog aanwezig</option>
              <option value="HERSTELD">Hersteld</option>
              <option value="WACHT_OP_ONDERDELEN">Wachtend op onderdelen</option>
            </select>
          </label>
          <label className="field"><span className="field-label">Schadekost</span><input className="input" type="number" value={form.damageCost} onChange={(e) => setForm({ ...form, damageCost: e.target.value })} /></label>
        </div>
        <label className="field" style={{ marginTop: 12 }}><span className="field-label">Beschrijving</span><textarea className="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <label className="field" style={{ marginTop: 12 }}>
          <span className="field-label">Foto's uploaden</span>
          <input
            id="damage-photos"
            className="checkout-file-input"
            type="file"
            accept="image/jpeg,image/png"
            capture="environment"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith("image/"));
              if (files.length) uploadMutation.mutate(files);
              e.target.value = "";
            }}
          />
          <label className="button checkout-camera-button" htmlFor="damage-photos">
            Foto's nemen/uploaden
          </label>
        </label>
        <div className="photo-grid" style={{ marginTop: 12 }}>
          {photos.map((photo) => (
            <div key={photo} className="photo-thumb-wrap">
              <img className="photo-thumb" src={`${BACKEND_URL}${photo}`} alt="Schade" />
              <button type="button" className="photo-delete-button" onClick={() => removePhoto(photo)} aria-label="Foto verwijderen">x</button>
            </div>
          ))}
        </div>
        <div className="action-row" style={{ marginTop: 14 }}>
          <button className="button" onClick={saveDamageCase}>
            {isCreating ? "Schadegeval aanmaken" : "Schadegeval opslaan"}
          </button>
          {!isCreating && selectedCase && (
            <button
              className="button danger"
              onClick={() => {
                if (window.confirm("Dit schadegeval archiveren? Het verdwijnt uit de actieve lijst.")) {
                  archiveMutation.mutate(selectedCase.id);
                }
              }}
            >
              Archiveren
            </button>
          )}
        </div>
      </aside>
      </div>
    </section>
  );
}
